/**
 * La zona gesto: il pezzo che porta la regola 2-ter.
 *
 * «Un gesto, un posto»: sotto la foto c'è sempre e solo l'azione successiva, ad
 * altezza fissa. Cambia il contenuto, non la posizione — ed è per questo che i
 * tre stati stanno in un componente solo invece che in tre pezzi montati altrove:
 * chi legge deve poter vedere in un colpo che occupano lo stesso posto.
 */

import { useState } from 'react';
import type { EsitoComando } from '@errebi/shared';
import type { BenchView, SizeView } from '@errebi/domain';

import type { creaBanco } from './api';
import { ArticleSearch } from './search-article';
import type { Modo } from './mode';

/**
 * La zona gesto: tre stati, stessa posizione.
 *
 * Nessun capo → i tasti di scansione. Capo ma nessuna cassetta → il codice
 * della cassetta. Cassetta aperta → la tastiera delle taglie.
 */
export function ZonaGesto({
  v,
  modo,
  setModo,
  quantita,
  setQuantita,
  fai,
  toccaTaglia,
  onAggancia,
  apriFotocamera,
  inCorso,
}: {
  v: BenchView;
  modo: Modo;
  setModo: (m: Modo) => void;
  /** How many garments the next tap is worth: empty means one. Digits only. */
  quantita: string;
  setQuantita: (q: string) => void;
  fai: (a: (b: ReturnType<typeof creaBanco>) => Promise<EsitoComando>) => () => void;
  toccaTaglia: (t: SizeView) => void;
  /** Scelta una riga dall'elenco, il capo si aggancia a quella. */
  onAggancia: (ordine: string, riga: string) => void;
  /** Apre la fotocamera sul capo agganciato (regola 38-bis). */
  apriFotocamera: () => void;
  inCorso: boolean;
}) {
  const [crateCode, setCrateCode] = useState('');
  const classe =
    'gesto' + (modo === 'meno' ? ' gesto--meno' : modo === 'fallato' ? ' gesto--fal' : '');

  return (
    <div className={classe}>
      <div className="gesto__top">
        {v.aperta ? (
          <>
            <span className="gesto__cass">{v.aperta.codice}</span>
            <span className={'gesto__fill' + (v.aperta.posti <= 4 ? ' gesto__fill--quasi' : '')}>
              {v.aperta.capi}/{v.capienza} capi
            </span>
          </>
        ) : (
          <span className="gesto__k">
            {v.agganciata ? 'Scegli la cassetta' : 'Comincia dal capo'}
          </span>
        )}
      </div>

      <div className="gesto__body">
        {/* 1 · nessun capo agganciato: si cerca l'articolo che si ha in mano.
             È lo **stesso componente** del pannello `?`, senza intestazione
             perché il titolo lo dice già la testata della zona: due posti, una
             ricerca, e nessuna coppia da tenere allineata. */}
        {!v.agganciata && (
          <ArticleSearch righe={v.righe} arrivo={v.arrivo} onAggancia={onAggancia} />
        )}

        {/* 2 · capo agganciato, nessuna cassetta aperta.
             The crates are office data, too many for a grid: the crate is
             scanned or typed, and buttons remain only for the crates this line
             already uses — reopening one is the case that wants speed. Whether
             a code exists is the server's answer (rule 6-bis), read in the
             notice like any other refusal. */}
        {v.agganciata && !v.aperta && (
          <>
            <form
              className="cerca"
              onSubmit={(e) => {
                e.preventDefault();
                const code = crateCode.trim().toUpperCase();
                if (code === '') return;
                setCrateCode('');
                fai((b) => b.apriCassetta(code))();
              }}
            >
              <label className="cerca__lab" htmlFor="codice-cassetta">
                Codice cassetta
              </label>
              <input
                id="codice-cassetta"
                className="cerca__campo"
                value={crateCode}
                onChange={(e) => setCrateCode(e.target.value)}
                placeholder="Leggi o scrivi, es. C121"
                autoComplete="off"
                autoFocus
                disabled={inCorso}
              />
              <button className="btn" type="submit" disabled={inCorso}>
                Apri
              </button>
            </form>
            {v.cassetteBanco.some((c) => c.diQuestaRiga) && (
              <div className="cassette">
                {v.cassetteBanco
                  .filter((c) => c.diQuestaRiga)
                  .map((c) => (
                    <button
                      key={c.codice}
                      className="cassetta cassetta--questa"
                      disabled={inCorso}
                      onClick={fai((b) => b.apriCassetta(c.codice))}
                    >
                      <span className="cassetta__cod">{c.codice}</span>
                      <span className="cassetta__n">
                        questa riga · {c.capi}/{v.capienza}
                      </span>
                    </button>
                  ))}
              </div>
            )}
          </>
        )}

        {/* 3 · cassetta aperta: la tastiera */}
        {v.aperta && (
          <>
            <div
              className={
                'tasti' +
                (modo === 'meno' ? ' tasti--meno' : modo === 'fallato' ? ' tasti--fal' : '')
              }
            >
              {v.taglie.map((t) => (
                <button
                  key={t.taglia}
                  className={
                    'tasto' + (t.chiusa ? ' tasto--ok' : t.ricevuti > 0 ? ' tasto--part' : '')
                  }
                  disabled={inCorso || (modo === null && t.chiusa)}
                  onClick={() => toccaTaglia(t)}
                  title={`${t.ricevuti}/${t.attesi} ricevuti${t.fallati ? ` · ${t.fallati} fallati` : ''}`}
                >
                  <span
                    className="tasto__fill"
                    style={{ height: `${t.attesi ? (t.ricevuti / t.attesi) * 100 : 0}%` }}
                  />
                  <span className="tasto__t">{t.taglia}</span>
                  <span className="tasto__q">
                    {t.ricevuti}/{t.attesi}
                  </span>
                  {t.fallati > 0 && <span className="tasto__x">✗{t.fallati}</span>}
                  {t.etichetta && <span className="tasto__et">×{t.etichetta.quantita}</span>}
                </button>
              ))}
            </div>

            <div className="mods">
              {/*
                The number sits before the modes because it is typed before the
                tap: «16», then the size. It turns off while a mode is armed —
                removing and defects go one garment at a time, and a live field
                there would suggest otherwise.
              */}
              <input
                className="mod mod__quanti"
                aria-label="Quantità"
                placeholder="Quanti"
                inputMode="numeric"
                autoComplete="off"
                value={quantita}
                disabled={modo !== null}
                onChange={(e) => setQuantita(e.target.value.replace(/\D/g, '').slice(0, 3))}
              />
              <button
                className="mod"
                aria-pressed={modo === 'meno'}
                onClick={() => setModo(modo === 'meno' ? null : 'meno')}
              >
                − correggi
              </button>
              <button
                className="mod mod--fal"
                aria-pressed={modo === 'fallato'}
                onClick={() => setModo(modo === 'fallato' ? null : 'fallato')}
              >
                ✗ fallato
              </button>
              <span className="mod__hint">
                {modo === 'meno'
                  ? 'Tocca la taglia da cui togliere un capo. Esc annulla.'
                  : modo === 'fallato'
                    ? 'Tocca la taglia del capo difettoso. Esc annulla.'
                    : quantita !== '' && Number(quantita) > 0
                      ? `Tocca la taglia: entrano ${quantita} capi in un colpo.`
                      : 'Un tocco sulla taglia è un capo trovato nella scatola.'}
              </span>
            </div>

            {/*
              La foto sta in questa barra e non nel corpo della zona gesto, ed
              è una scelta sulla regola 2-ter: lì va **l'azione successiva**, e
              l'azione successiva è contare. Questa barra è già la fila delle
              cose che si fanno *alla cassetta aperta* — stampa, conferma,
              svuota — e la foto è esattamente questo: una volta per cassetta,
              non una per capo. La richiesta compare **qui accanto**, dove sta
              anche il gesto: chiedere in un posto e agire in un altro sarebbe
              aggirare la 2-ter, non applicarla.
            */}
            <div className="mods mods--stampa">
              <button
                className={v.aperta?.fotoAttesa === true ? 'mod mod--foto' : 'mod'}
                disabled={inCorso}
                onClick={apriFotocamera}
              >
                {v.aperta?.fotoAttesa === true ? '📷 Scatta la foto' : '📷 Foto'}
              </button>
              {/*
                Un tasto solo, due gesti, e non è una scorciatoia: è sempre
                «la carta di questa cassetta». Finché manca un foglio stampa
                quelli che mancano; quando ci sono tutti diventa verde e li
                rifà con gli stessi id. Prima diceva «Etichette a posto» ed era
                spento — un vicolo cieco, esattamente come quello che la
                fotocamera evita: il foglio si strappa, la stampante è spenta,
                la carta esce storta, e la ristampa al banco serve.
              */}
              <button
                className={'btn' + (v.stampaDovuta.length === 0 ? ' btn--fatto' : '')}
                disabled={inCorso}
                onClick={fai((b) =>
                  v.stampaDovuta.length === 0
                    ? b.ristampaEtichette(v.aperta!.codice)
                    : b.stampaEtichette(v.aperta!.codice),
                )}
              >
                {v.stampaDovuta.length === 0
                  ? '✓ Etichette stampate · ristampa'
                  : `Stampa ${v.stampaDovuta.length} etichett${v.stampaDovuta.length === 1 ? 'a' : 'e'} · ${v.stampaDovuta.join(', ')}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
